import { useCallback } from "react"
import { Plus, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button, Input } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { MAX_SUGGESTED_QUESTIONS, MAX_SUGGESTED_QUESTION_LENGTH } from "./type"

export interface SuggestedQuestionsFieldProps {
  questions: string[]
  onQuestionsChange: (questions: string[]) => void
  disabled?: boolean
  className?: string
}

export const SuggestedQuestionsField = ({
  questions,
  onQuestionsChange,
  disabled = false,
  className,
}: SuggestedQuestionsFieldProps) => {
  const { t } = useTranslation()

  const handleAdd = useCallback(() => {
    if (questions.length >= MAX_SUGGESTED_QUESTIONS) return
    onQuestionsChange([...questions, ""])
  }, [questions, onQuestionsChange])

  const handleRemove = useCallback(
    (index: number) => {
      const newQuestions = questions.filter((_, i) => i !== index)
      onQuestionsChange(newQuestions)
    },
    [questions, onQuestionsChange]
  )

  const handleChange = useCallback(
    (index: number, value: string) => {
      // Limit to max length
      const trimmedValue = value.slice(0, MAX_SUGGESTED_QUESTION_LENGTH)
      const newQuestions = [...questions]
      newQuestions[index] = trimmedValue
      onQuestionsChange(newQuestions)
    },
    [questions, onQuestionsChange]
  )

  const canAddMore = questions.length < MAX_SUGGESTED_QUESTIONS

  return (
    <div className={cn("space-y-2", className)}>
      {questions.map((question, index) => (
        <div key={index} className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Input
              value={question}
              onChange={(e) => handleChange(index, e.target.value)}
              placeholder={t(
                "workspace.agent.basicForm.questionPlaceholder",
                "Enter a question..."
              )}
              disabled={disabled}
              className="pr-16"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              {question.length}/{MAX_SUGGESTED_QUESTION_LENGTH}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => handleRemove(index)}
            disabled={disabled}
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {canAddMore && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("workspace.agent.basicForm.addQuestion", "Add Question")}
        </Button>
      )}

      {!canAddMore && (
        <p className="text-xs text-muted-foreground text-center">
          {t("workspace.agent.basicForm.maxQuestionsReached", {
            max: MAX_SUGGESTED_QUESTIONS,
            defaultValue: `Maximum ${MAX_SUGGESTED_QUESTIONS} questions allowed`,
          })}
        </p>
      )}
    </div>
  )
}

export default SuggestedQuestionsField
